import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import MetaTags from '@/components/SEO/MetaTags';
import { 
  ChevronLeft, 
  Heart, 
  Sparkles, 
  Shield, 
  Users, 
  Globe, 
  CheckCircle2,
  ArrowRight,
  Activity,
  Clock,
  FileText
} from 'lucide-react';

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-cream-paper py-8 sm:py-12 px-4 sm:px-6">
      <MetaTags
        title="About ByOnco · Cancer Care Navigation & Matching"
        description="Learn about ByOnco's mission to bring clinical clarity, explainable US cancer hospital matching, and transparent treatment costs to patients worldwide."
        path="/about"
      />
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          className="text-forest-ink hover:text-forest-shadow hover:bg-keylime-wash mb-6"
          onClick={() => navigate('/')}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>

        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16 px-2"
        >
          <Badge className="mb-4 bg-keylime-wash text-charcoal border-border-mist px-4 py-1.5 text-sm font-semibold">
            <Heart className="h-3 w-3 mr-1.5 inline" />
            Our Story
          </Badge>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-forest-ink mb-4 sm:mb-6 break-words">
            Why ByOnco Exists
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-charcoal max-w-3xl mx-auto leading-relaxed">
            We believe every cancer patient deserves access to the best care, regardless of where they live or what they can afford. 
            ByOnco was born from real pain and real system gaps, and built to solve them.
          </p>
        </motion.div>

        {/* Origin Story */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-12 sm:mb-16"
        >
          <Card className="bg-keylime-wash border border-border-mist">
            <CardContent className="p-6 sm:p-8 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-keylime-wash border-2 border-border-mist rounded-full h-12 w-12 flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-forest-ink" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-light text-forest-ink">The Origin Story</h2>
              </div>
              <div className="space-y-4 text-charcoal text-base sm:text-lg leading-relaxed">
                <p>
                  ByOnco was founded by a team that experienced firsthand the challenges of navigating cancer care. 
                  We watched families struggle to find the right hospitals, understand costs, access second opinions, 
                  and coordinate care across cities and countries.
                </p>
                <p>
                  The healthcare system, while filled with dedicated professionals, often lacks the transparency, 
                  the transparency and coordination tools that patients desperately need during their most vulnerable moments.
                </p>
                <p>
                  We saw the gap between what technology could enable and what patients were experiencing. 
                  So we built ByOnco as a trusted navigation partner 
                  that empowers patients and families with clarity, speed, and hope.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.section>

        {/* What We Do */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-12 sm:mb-16"
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-light text-forest-ink mb-4">
              What We Do
            </h2>
            <p className="text-charcoal text-lg max-w-2xl mx-auto">
              ByOnco pairs smart matching with human care coordination to help cancer patients find and access treatment.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                icon: <Activity className="h-6 w-6 text-forest-ink" />,
                title: "Oncology Navigation",
                description: "We review your cancer type, stage, location, and preferences to match you with verified centres globally."
              },
              {
                icon: <FileText className="h-6 w-6 text-forest-ink" />,
                title: "Rapid Second Opinions",
                description: "Connect with board-certified oncologists for comprehensive written second opinions, target delivery within 12 hours."
              },
              {
                icon: <Globe className="h-6 w-6 text-forest-ink" />,
                title: "Cost Clarity",
                description: "Transparent cost estimates, financial planning, subsidy matching, and complete care package coordination."
              }
            ].map((feature, index) => (
              <Card key={index} className="bg-keylime-wash border border-border-mist">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-keylime-wash border border-border-mist rounded-lg h-12 w-12 flex items-center justify-center flex-shrink-0">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-normal text-forest-ink mb-2">{feature.title}</h3>
                      <p className="text-charcoal leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.section>

        {/* What Makes Us Different */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-12 sm:mb-16"
        >
          <Card className="bg-sage-mist border border-border-mist">
            <CardContent className="p-6 sm:p-8 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-keylime-wash border-2 border-border-mist rounded-full h-12 w-12 flex items-center justify-center">
                  <Shield className="h-6 w-6 text-forest-ink" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-light text-forest-ink">What Makes Us Different</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  {
                    title: "Data Integrity",
                    description: "We publish hospital fields we can source, and we show the gap when we cannot. No invented ratings, wait times, or bed counts."
                  },
                  {
                    title: "Verified Centre Profiles",
                    description: "Live bed and queue feeds are not connected yet. Contact the hospital directly for current capacity."
                  },
                  {
                    title: "Patient-First Coordination",
                    description: "Every care package is tailored to your needs. We coordinate travel, accommodation, translation, and local support."
                  },
                  {
                    title: "Trust & Transparency",
                    description: "Clear pricing, honest recommendations, and full disclosure of what's included in every service and package."
                  }
                ].map((point, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-forest-ink flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-lg font-normal text-forest-ink mb-1">{point.title}</h3>
                      <p className="text-charcoal leading-relaxed">{point.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.section>

        {/* How It Works */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mb-12 sm:mb-16"
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-light text-forest-ink mb-4">
              How It Works
            </h2>
            <p className="text-charcoal text-lg max-w-2xl mx-auto">
              Getting started with ByOnco is simple. We guide you through every step.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                step: "1",
                title: "Share Your Journey",
                description: "Tell us about your cancer type, stage, location, budget, and preferences. We review your needs instantly."
              },
              {
                step: "2",
                title: "Get Matched",
                description: "Receive personalized hospital recommendations from verified centre profiles."
              },
              {
                step: "3",
                title: "Access Care",
                description: "Connect with hospitals, get second opinions, coordinate travel, and access financial support, all in one place."
              }
            ].map((step, index) => (
              <Card key={index} className="bg-keylime-wash border border-border-mist">
                <CardContent className="p-6 text-center">
                  <div className="bg-forest-ink rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-light text-cream-paper">{step.step}</span>
                  </div>
                  <h3 className="text-xl font-normal text-forest-ink mb-3">{step.title}</h3>
                  <p className="text-charcoal leading-relaxed">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.section>

        {/* Trust & Safety */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mb-12 sm:mb-16"
        >
          <Card className="bg-keylime-wash border border-border-mist">
            <CardContent className="p-6 sm:p-8 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-keylime-wash border-2 border-border-mist rounded-full h-12 w-12 flex items-center justify-center">
                  <Shield className="h-6 w-6 text-forest-ink" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-light text-forest-ink">Trust & Safety</h2>
              </div>
              <div className="space-y-4 text-charcoal text-base sm:text-lg leading-relaxed">
                <p>
                  <strong className="text-forest-ink">Medical Disclaimer:</strong> ByOnco is a healthcare navigation and coordination 
                  platform. We are not a medical provider and do not provide medical advice, diagnosis, or treatment. All medical 
                  decisions must be made in consultation with qualified healthcare professionals.
                </p>
                <p>
                  Your privacy and data security are paramount. We comply with India's Digital Personal Data Protection Act, 2023, 
                  and maintain HIPAA-equivalent security standards. Learn more in our{' '}
                  <a href="/privacy" className="text-forest-ink hover:text-charcoal hover:underline">Privacy Policy</a> and{' '}
                  <a href="/security" className="text-forest-ink hover:text-charcoal hover:underline">Security</a> pages.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.section>

        {/* CTA Section */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="text-center"
        >
          <Card className="bg-sage-mist border border-border-mist">
            <CardContent className="p-8 sm:p-10 md:p-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-light text-forest-ink mb-4">
                Ready to Start Your Journey?
              </h2>
              <p className="text-charcoal text-lg mb-8 max-w-2xl mx-auto">
                Join thousands of families who found clarity, hope, and faster treatment starts with ByOnco.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  className="bg-forest-ink hover:bg-forest-shadow text-cream-paper px-8 py-6 text-lg"
                  onClick={() => navigate('/get-started')}
                >
                  Get Started <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-border-mist text-charcoal hover:bg-keylime-wash px-8 py-6 text-lg"
                  onClick={() => navigate('/app')}
                >
                  Talk to ByOnco AI <Clock className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.section>
      </div>
    </div>
  );
}

